import WatchlistRow from "./WatchlistRow";
import { useDispatch, useSelector } from "react-redux";
import {
  setWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  setLoading,
  setError,
  setSelectedSymbol,
} from "../../../redux/watchlistSlice";
import {
  useGetUserWatchlistQuery,
  useAddRemoveSymbolMutation,
} from "../../../redux/exchangeApi";
import React, { useEffect, useState, useMemo } from "react";
import AssetModel from "../AssetModel";
import { setInitialPrices } from "../../../redux/marketSlice";

const WatchlistPanel = ({ socket }) => {
  const userData = useSelector((state) => state?.user?.userData);
  const prices = useSelector((state) => state.market.prices);
  const watchlist = useSelector((state) => state.watchlist.items);
  const selectedSymbol = useSelector((state) => state.watchlist.selectedSymbol);
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPairId, setSelectedPairId] = useState("");

  const { data, isLoading, error, refetch } = useGetUserWatchlistQuery(
    userData?.id
  );
  const [toggleSymbol, { isLoading: updatingWatch }] =
    useAddRemoveSymbolMutation();

  const {
    is_crypto_enabled,
    is_crypto_future_enabled,
    is_equity_enabled,
    is_futures_enabled,
    is_fx_enabled,
  } = userData || {};

  useEffect(() => {
    if (isLoading) {
      dispatch(setLoading());
    } else if (error) {
      dispatch(setError());
    } else if (data) {
      const storedSelection = localStorage.getItem("selectedSymbol");
      const parsed = storedSelection ? JSON.parse(storedSelection) : null;

      const storedSymbolExists =
        parsed && data.some((d) => d.symbol === parsed.symbol);

      // Fallback: if no stored or invalid symbol
      if (!storedSymbolExists && data.length > 0) {
        const defaultSymbol = data[0];

        // Overwrite invalid localStorage value
        dispatch(
          setSelectedSymbol({
            symbol: defaultSymbol.symbol,
            segment: defaultSymbol.platform,
            sellCommission: defaultSymbol.sell_commission,
            buyCommission: defaultSymbol.buy_commission,
            commissionType: defaultSymbol.commission_type,
            priceStep: defaultSymbol.priceDecimal,
            stepSize: defaultSymbol.stepSize,
            contractSize: defaultSymbol.contract_size,
          })
        );
      } else if (storedSymbolExists) {
        // Re-apply valid stored value (if not already applied by Redux)
        dispatch(
          setSelectedSymbol({
            symbol: parsed.symbol,
            segment: parsed.segment,
            sellCommission: parsed.buyCommission, // might be flipped in your storage logic
            buyCommission: parsed.sellCommission,
            commissionType: parsed.commissionType,
            priceStep: parsed.priceStep,
            stepSize: parsed.stepSize,
            contractSize: parsed.contractSize,
          })
        );
      }

      // Always set watchlist + prices
      dispatch(setWatchlist(data));
      dispatch(setInitialPrices(data));
    }
  }, [data, isLoading, error, dispatch]);

  const handleToggleSymbol = async (symbol, action, segment) => {
    setSelectedPairId(symbol);

    const result = await toggleSymbol({
      segment,
      brokerId: userData?.refred_by_id,
      symbol,
      action,
    });

    if (result?.data?.status) {
      refetch();

      // socket.emit("subscribe_watchlist", {
      //   userId: userData.id,
      //   brokerId: userData.refred_by_id,
      // });

      if (action === "add") {
        dispatch(addToWatchlist({ symbol, instrument_id: null }));
      } else {
        dispatch(removeFromWatchlist(symbol));

        const currentSelection = JSON.parse(
          localStorage.getItem("selectedSymbol")
        );
        if (currentSelection?.symbol === symbol) {
          localStorage.removeItem("selectedSymbol");

          const updatedList = data.filter((item) => item.symbol !== symbol);
          if (updatedList.length > 0) {
            const fallbackSymbol = updatedList[0];
            dispatch(
              setSelectedSymbol({
                symbol: fallbackSymbol.symbol,
                segment: fallbackSymbol.platform,
                sellCommission: fallbackSymbol.sell_commission,
                buyCommission: fallbackSymbol.buy_commission,
                commissionType: fallbackSymbol.commission_type,
                priceStep: fallbackSymbol.priceDecimal,
                stepSize: fallbackSymbol.stepSize,
                contractSize: fallbackSymbol.contract_size,
              })
            );
          }
        }
      }

      setSelectedPairId("");
    } else {
      setSelectedPairId("");
    }
  };

  const groupedWatchlist = useMemo(() => {
    const list = {};
    if (is_equity_enabled === "yes") {
      list.Stocks = watchlist.filter(
        (item) => item.platform === "equity" || item.platform === "india_equity"
      );
    }
    if (is_fx_enabled === "yes") {
      list.Forex = watchlist.filter((item) => item.platform === "forex");
    }
    if (is_crypto_enabled === "yes") {
      list.Crypto = watchlist.filter((item) => item.platform === "crypto");
    }
    if (is_futures_enabled === "yes") {
      list.Futures = watchlist.filter((item) => item.platform === "futures");
    }
    if (is_crypto_future_enabled === "yes") {
      list["Crypto Futures"] = watchlist.filter(
        (item) => item.platform === "crypto_futures"
      );
    }
    return list;
  }, [
    is_crypto_enabled,
    is_crypto_future_enabled,
    is_equity_enabled,
    is_futures_enabled,
    is_fx_enabled,
    watchlist,
  ]);

  const renderWatchlistRows = (categoryItems) =>
    categoryItems.map((item) => {
      const priceData = prices[item.symbol] || {};
      return (
        <WatchlistRow
          stepSize={item.stepSize}
          priceStep={item.priceDecimal}
          key={item.symbol}
          active={selectedSymbol === item.symbol}
          symbol={item.symbol}
          bid={priceData.bid || item.best_bid || 0}
          ask={priceData.ask || item.best_ask || 0}
          isDefault={item.is_default === "Y"}
          icon={item.icon}
          change={priceData.changePer || item.changePer || 0}
          removeWatchListSymbol={handleToggleSymbol}
          segment={item.platform}
          isUpdating={updatingWatch}
          sellCommission={item.sell_commission}
          buyCommission={item.buy_commission}
          commissionType={item.commission_type}
          contractSize={item.contract_size}
        />
      );
    });

  return (
    <div className="dash-grid-two">
      <div className="ne_box bg2  border_box d_new_right">
        <div className="panel-heading tab-box p-2 d-flex">
          <h6 className="text-left drag-handle mr-auto">Watchlist</h6>
          <i
            className="bi bi-plus-lg cursor-pointer"
            onClick={() => setIsModalOpen(true)}
            style={{ fontSize: "18px" }}
          />
        </div>
        <div className="trading_scrool ">
          <table className="table table-hover currency_table2">
            <thead>
              <tr>
                <th>Symbol</th>
                <th className="text-right">Best BID</th>
                <th className="text-right">Best ASK</th>
                <th className="text-right">Chg%</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedWatchlist).map(([category, items]) => (
                <React.Fragment key={category}>
                  {items.length > 0 && (
                    <tr>
                      <td
                        colSpan="4"
                        className="font-weight-bold text-uppercase text-muted"
                      >
                        {category}
                      </td>
                    </tr>
                  )}
                  {renderWatchlistRows(items)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <AssetModel
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        segment="crypto"
        added={watchlist.map((item) => item.symbol)}
        getSymbol={refetch}
        updateUserWatchList={handleToggleSymbol}
        selectedPairId={selectedPairId}
        updatingWatch={updatingWatch}
      />
    </div>
  );
};

export default WatchlistPanel;
